<script module lang="ts">
  let seq = 0;
</script>

<script lang="ts">
  /**
   * To/Cc/Bcc field (spec §11 "AddressField"): address chips plus a combobox input with
   * suggestions from `store.suggest(text, 6)` once two characters are typed. `,` / `;` / Enter /
   * blur commit the typed text; only `ADDRESS_RE` matches become chips; duplicates are ignored;
   * Backspace on an empty input removes the last chip.
   */
  import { tick } from 'svelte';
  import { t } from '$lib/i18n';
  import { endsWithSeparator, isValidAddress } from '$lib/mail/compose';
  import { store } from '$lib/store';
  import { tooltip } from '$lib/util/dom';
  import Icon from '../ui/Icon.svelte';

  let {
    addrs = $bindable([]),
    label,
    kind,
    onchange,
  }: { addrs?: string[]; label: string; kind: 'to' | 'cc' | 'bcc'; onchange?: () => void } = $props();

  let text = $state('');
  let sugg = $state<{ name: string; email: string }[]>([]);
  let idx = $state(-1);
  let inputEl = $state<HTMLElement | undefined>();
  /** Sequence of the latest suggest() call so a slow earlier answer cannot overwrite a newer one. */
  let query = 0;

  const id = `tik-addr-${++seq}`;
  const optId = (i: number) => `${id}-opt-${i}`;

  /** Focus the input (used by the Ctrl/Meta+Shift+C / B chords). */
  export function focus(): void {
    inputEl?.focus();
  }

  function closeSugg(): void {
    sugg = [];
    idx = -1;
  }

  /** Commit `value` (or the typed text) as a chip: trailing separators stripped, invalid kept as text. */
  function commit(value?: string): void {
    const v = (value ?? text).trim().replace(/[,;]+$/, '').trim();
    if (!v) {
      closeSugg();
      return;
    }
    if (!isValidAddress(v)) return;
    if (!addrs.includes(v)) {
      addrs = [...addrs, v];
      onchange?.();
    }
    text = '';
    closeSugg();
  }

  async function onInput(): Promise<void> {
    if (endsWithSeparator(text)) {
      commit();
      return;
    }
    const needle = text.trim();
    const mine = ++query;
    const list = needle.length >= 2 ? await store.suggest(needle, 6) : [];
    if (mine !== query) return;
    sugg = list;
    idx = -1;
  }

  async function move(to: number): Promise<void> {
    const n = sugg.length;
    idx = ((to % n) + n) % n;
    await tick();
    document.getElementById(optId(idx))?.scrollIntoView?.({ block: 'nearest' });
  }

  function onKey(ev: KeyboardEvent): void {
    if (sugg.length) {
      switch (ev.key) {
        case 'ArrowDown':
          ev.preventDefault();
          void move(idx + 1);
          return;
        case 'ArrowUp':
          ev.preventDefault();
          void move(idx - 1);
          return;
        case 'Home':
          ev.preventDefault();
          void move(0);
          return;
        case 'End':
          ev.preventDefault();
          void move(sugg.length - 1);
          return;
        case 'Escape':
          ev.preventDefault();
          ev.stopPropagation();
          closeSugg();
          return;
        case 'Enter':
          if (idx >= 0) {
            ev.preventDefault();
            commit(sugg[idx]!.email);
            return;
          }
      }
    }
    if ((ev.key === 'Enter' || ev.key === ',' || ev.key === ';') && text.trim()) {
      ev.preventDefault();
      commit();
    } else if (ev.key === 'Backspace' && !text && addrs.length) {
      addrs = addrs.slice(0, -1);
      onchange?.();
    }
  }

  function remove(addr: string): void {
    addrs = addrs.filter((a) => a !== addr);
    onchange?.();
  }
</script>

<div class="awrap">
  <div class="afield" data-kind={kind}>
    <span class="alabel">{label}</span>
    <div class="achips">
      {#each addrs as addr (addr)}
        <span class="achip" dir="ltr">
          {addr}
          <button
            type="button"
            aria-label={t('del')}
            use:tooltip
            onclick={(ev) => {
              ev.stopPropagation();
              remove(addr);
            }}><Icon name="x" size={12} /></button>
        </span>
      {/each}
      <input
        bind:this={inputEl}
        type="text"
        dir="ltr"
        autocomplete="off"
        spellcheck="false"
        role="combobox"
        aria-autocomplete="list"
        aria-expanded={sugg.length > 0}
        aria-controls={id}
        aria-activedescendant={idx >= 0 ? optId(idx) : undefined}
        placeholder={addrs.length ? '' : 'user@example.com'}
        aria-label={label}
        bind:value={text}
        oninput={onInput}
        onkeydown={onKey}
        onblur={() => commit()}
      />
    </div>
  </div>
  {#if sugg.length}
    <div class="sugg" role="listbox" {id}>
      {#each sugg as s, i (s.email)}
        <button
          type="button"
          role="option"
          id={optId(i)}
          class:on={i === idx}
          aria-selected={i === idx}
          onpointerdown={(ev) => ev.preventDefault()}
          onclick={() => commit(s.email)}
        >
          <strong dir="auto">{s.name || s.email}</strong>
          {#if s.name}<small dir="ltr">{s.email}</small>{/if}
        </button>
      {/each}
    </div>
  {/if}
</div>

<style>
  /* ===== component: svelte-z6tbtz (AddressField (To/Cc/Bcc chips + suggestions)) ===== */
  .awrap {
    position: relative;
    flex: 1;
    min-width: 0;
    display: grid;
  }

  .afield {
    display: flex;
    align-items: flex-start;
    gap: var(--space-3);
    min-height: var(--control-lg);
    padding-block: var(--space-1);
    cursor: text;
  }

  .alabel {
    color: var(--text-faint);
    font-size: var(--text-sm);
    min-width: 44px;
    padding-block-start: var(--space-2);
  }

  .achips {
    flex: 1;
    min-width: 0;
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    gap: var(--space-2);
  }

  .achip {
    display: inline-flex;
    align-items: center;
    gap: var(--space-1);
    background: var(--blue-50);
    color: var(--blue-700);
    border: 1px solid var(--blue-100);
    border-radius: var(--radius-pill);
    padding-block: var(--space-1);
    padding-inline: var(--space-3) var(--space-1);
    font-size: var(--text-xs);
    max-width: 100%;
    overflow-wrap: anywhere;
  }

  :global(html.tik-dark) .achip {
    background: #4a7cf629;
    color: var(--blue-200);
    border-color: #4a7cf64d;
  }

  .achip button {
    display: grid;
    place-items: center;
    width: var(--control-xs);
    height: var(--control-xs);
    border-radius: var(--radius-pill);
    color: inherit;
    opacity: 0.7;
  }

  .achip button:hover {
    opacity: 1;
    background: var(--surface-hover);
  }

  .achips input {
    flex: 1;
    min-width: 88px;
    border: 1px solid transparent;
    background: none;
    outline: none;
    border-radius: var(--radius-control);
    color: var(--text-body);
    height: var(--control-sm);
  }

  .achips input:focus-visible {
    border-color: var(--focus-border);
    box-shadow: var(--ring-focus);
  }

  .sugg {
    position: absolute;
    inset-block-start: calc(100% + var(--space-1));
    inset-inline: 0;
    z-index: var(--z-layer);
    display: grid;
    padding: var(--space-1);
    background: var(--surface-raised);
    border: 1px solid var(--border-default);
    border-radius: var(--radius-md);
    box-shadow: var(--shadow-panel);
    max-block-size: min(340px, 60dvh);
    overflow-y: auto;
  }

  .sugg button {
    display: flex;
    align-items: baseline;
    gap: var(--space-2);
    padding-block: var(--space-2);
    padding-inline: var(--space-3);
    border-radius: var(--radius-sm);
    text-align: start;
  }

  .sugg button:hover,
  .sugg button.on {
    background: var(--surface-sunken);
  }

  .sugg strong {
    flex: 1;
    min-inline-size: 0;
    color: var(--text-strong);
    font-weight: var(--weight-medium);
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  .sugg small {
    flex: none;
    max-inline-size: 45%;
    color: var(--text-faint);
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  /* appended global fixes (2026-09-04): recipient row — the field may shrink */
  .afield,
  .achips {
    min-width: 0;
  }

  .achips input {
    min-width: 0;
    flex: 1 1 6ch;
    width: 0;
  }
</style>
